/**
 * Resolves effective participants for a track (cycle + track, users + groups).
 * Used for participant counts, facilitator picker, and value-provider selection.
 */

import { useState, useEffect, useMemo } from 'react';
import { resolveEffectiveParticipantsForTrack, type EffectiveParticipant } from '../lib/effectiveParticipantResolver';
import { getMemberships } from '../lib/groupsService';
import { useCycleParticipantsStore } from '../stores/prioritization/cycleParticipantsStore';
import { useCycleParticipantGroupsStore } from '../stores/prioritization/cycleParticipantGroupsStore';
import { useTrackParticipantsStore } from '../stores/prioritization/trackParticipantsStore';
import { useTrackParticipantGroupsStore } from '../stores/prioritization/trackParticipantGroupsStore';

export function useEffectiveTrackParticipants(cycleId: string | undefined, trackId: string | undefined) {
  const [effective, setEffective] = useState<EffectiveParticipant[]>([]);
  const [loading, setLoading] = useState(true);

  const cycleParticipants = useCycleParticipantsStore(s => s.participants);
  const cycleGroups = useCycleParticipantGroupsStore(s => s.participantGroups);
  const trackParticipants = useTrackParticipantsStore(s => s.participants);
  const trackGroups = useTrackParticipantGroupsStore(s => s.participantGroups);

  const cycleDirect = useMemo(() => {
    if (!cycleId) return [];
    return cycleParticipants
      .filter(p => p.cycleId === cycleId && p.status === 'active')
      .map(p => ({ userId: p.userId }));
  }, [cycleId, cycleParticipants]);

  const cycleGroupList = useMemo(() => {
    if (!cycleId) return [];
    return cycleGroups
      .filter(g => g.cycleId === cycleId && g.status === 'active')
      .map(g => ({ groupId: g.groupId }));
  }, [cycleId, cycleGroups]);

  const trackDirect = useMemo(() => {
    if (!trackId) return [];
    return trackParticipants
      .filter(p => p.trackId === trackId && p.status === 'active')
      .map(p => ({ userId: p.userId }));
  }, [trackId, trackParticipants]);

  const trackGroupList = useMemo(() => {
    if (!trackId) return [];
    return trackGroups
      .filter(g => g.trackId === trackId && g.status === 'active')
      .map(g => ({ groupId: g.groupId }));
  }, [trackId, trackGroups]);

  useEffect(() => {
    if (!cycleId || !trackId) {
      setEffective([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    resolveEffectiveParticipantsForTrack({
      cycleDirectUsers: cycleDirect,
      cycleParticipantGroups: cycleGroupList,
      trackDirectUsers: trackDirect,
      trackParticipantGroups: trackGroupList,
      getMemberUserIdsForGroup: async (groupId: string) => {
        const memberships = await getMemberships({ groupId });
        return [...new Set(memberships.map(m => m.userId).filter(Boolean))];
      },
    })
      .then(setEffective)
      .catch(() => setEffective([]))
      .finally(() => setLoading(false));
  }, [cycleId, trackId, cycleDirect, cycleGroupList, trackDirect, trackGroupList]);

  const effectiveUserIds = useMemo(() => effective.map(e => e.userId), [effective]);
  const effectiveCount = effective.length;
  return { effective, effectiveUserIds, effectiveCount, loading };
}
